// ============================================================================
// screenshots 那一列 → 截圖卡片要的欄位
//
// 檢查工作站的縮圖列與報告工作站的選圖卡片都要做這個轉換。兩邊各寫一次的話，
// DB 欄位改名時（或 landmarkID 的 0/NULL 慣例改變時）會只改到一邊，而畫面上
// 看起來只是「有一邊沒顯示位置」——不會有錯誤訊息。所以只有這一份。
// ============================================================================

import { landmarkAbbr } from '@/utils/landmarkAbbr'
import {
  clinicalAnnotationText,
  firstLesion,
  parseClinicalAnnotations,
} from '@/utils/clinicalAnnotations'

function canonicalDecimal(value) {
  const match = String(value).trim().match(/^(\d+)(?:\.(\d+))?$/)
  if (!match) return null
  const whole = match[1].replace(/^0+(?=\d)/, '')
  const fraction = (match[2] || '').replace(/0+$/, '')
  return fraction ? `${whole}.${fraction}` : whole
}

function millimetersToCentimeters(value) {
  const match = String(value).trim().match(/^(\d+)(?:\.(\d+))?$/)
  if (!match) return null
  const fractionLength = (match[2] || '').length
  let digits = `${match[1]}${match[2] || ''}`.replace(/^0+(?=\d)/, '')
  const scale = fractionLength + 1
  digits = digits.padStart(scale + 1, '0')
  const splitAt = digits.length - scale
  const whole = digits.slice(0, splitAt).replace(/^0+(?=\d)/, '') || '0'
  const fraction = digits.slice(splitAt).replace(/0+$/, '')
  return fraction ? `${whole}.${fraction}` : whole
}

function normalizeNumericDistance(value, convert = canonicalDecimal) {
  const match = String(value).trim().match(/^(\d+(?:\.\d+)?)(?:\s*(?:-|~|～|至)\s*(\d+(?:\.\d+)?))?$/)
  if (!match) return null
  const start = convert(match[1])
  const end = match[2] == null ? null : convert(match[2])
  if (start == null || (match[2] != null && end == null)) return null
  return end == null ? start : `${start}~${end}`
}

/** 將 DB 或 legacy distance 明確呈現為 canonical cm；無法判讀的 mm 原文不錯譯。 */
export function formatDistanceCm(value) {
  const text = String(value == null ? '' : value).trim()
  if (!text) return ''
  const unitMatch = text.match(/^(.*?)\s*(cm|公分|mm|毫米|公釐)\s*$/i)
  if (!unitMatch) {
    if (/\bmm\b|毫米|公釐/i.test(text)) return text
    return `${normalizeNumericDistance(text) || text} cm`
  }
  const numeric = unitMatch[1].trim()
  const unit = unitMatch[2].toLowerCase()
  if (unit === 'mm' || unit === '毫米' || unit === '公釐') {
    const centimeters = normalizeNumericDistance(numeric, millimetersToCentimeters)
    return centimeters == null ? text : `${centimeters} cm`
  }
  const centimeters = normalizeNumericDistance(numeric)
  return centimeters == null ? text : `${centimeters} cm`
}

/**
 * @typedef {Object} ShotCardFields
 * @property {number|null} serial       imgSerial（卡片左上的 P 編號）
 * @property {string} lesionName        病灶名，未標註時空字串
 * @property {string} distanceText      距肛門距離，例 '16 cm'
 * @property {string} sizeText          大小，例 '5~10 mm'
 * @property {string} locationAbbr      位置縮寫，例 'A. colon'
 * @property {string} locationFull      位置全名（給 title 用，縮寫反推不回來）
 * @property {number} landmarkID        正式部位 id；編輯器保存其他欄位時也要原樣帶回
 */

/**
 * @param {Object} img            screenshots 的一列（examinationData.all 的元素）
 * @param {Array<{id:number, landmark:string}>} landmarkList  store 的 landmarkList
 * @returns {ShotCardFields}
 */
export function shotCardFields(img, landmarkList) {
  const row = img || {}
  const legacySize = String(row.evas2_size_text || '').trim()
  const legacyDistance = legacySize.match(/^distance\s+(.+)$/i)
  const rawDistance = String(row.cm || (legacyDistance && legacyDistance[1]) || '').trim()
  const distanceText = formatDistanceCm(rawDistance)

  // landmarkID 的「未指定」有兩種寫法：舊版 insert 寫 0、新欄位慣例用 NULL。
  // 兩種都要當成沒有位置，否則會拿 0 去查主檔、查不到再顯示空字串——結果一樣
  // 但多繞一圈，而且 0 是個看起來像有效 id 的值，日後容易被誤判。
  const landmarkID = Number(row.landmarkID) || 0
  const hit =
    landmarkID && Array.isArray(landmarkList)
      ? landmarkList.find((l) => Number(l.id) === landmarkID)
      : null
  const locationFull = (hit && hit.landmark) || ''
  const clinicalAnnotations = parseClinicalAnnotations(
    row.evas2_lesion_name,
    row.evas2_annotations,
  )

  // ⚠️ 這裡刻意**不**回傳 evas2_finding_no。卡片上不再顯示 finding 編號（理由見
  //    ShotInfoCard 的註解）。那一欄還在、也還在用，但只由報告工作站的指派下拉
  //    讀寫（Report2DetailPage 直接讀 img.evas2_finding_no），不經過這支映射。
  return {
    serial: row.imgSerial == null ? null : Number(row.imgSerial),
    lesionName: firstLesion(clinicalAnnotations),
    clinicalAnnotations,
    annotationText: clinicalAnnotationText(clinicalAnnotations),
    // cm 是既有 schema 專門承載距肛門距離的欄位。舊版曾把 distance 寫進
    // evas2_size_text；雙讀讓升級不需破壞性 migration，也能直接回滾。
    distanceText,
    sizeText: legacyDistance ? '' : legacySize,
    landmarkID,
    locationAbbr: landmarkAbbr(locationFull),
    locationFull,
  }
}
